import { Box } from "@mui/material";
import { isFulfilled, nanoid } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../app/hooks";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { PanelLayout } from "../../../../common/components/functional/panelLayout/PanelLayout";
import isEqual from "../../../../common/helpers/isEqual";
import { useConfirmationDialog } from "../../../../contexts/ConfirmationDialogContext";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";
import useIsLoading from "../../../../hooks/useIsLoading";
import { OrderType } from "../../orderAPI";
import { generateOrderIdempotencyKey } from "../../orderIdempotencyKey";
import {
  getStoredIdempotencyHash,
  storeIdempotencyHash,
} from "../../orderIdempotencyStorage";
import { addNewLongOrder } from "../../orderRowsSlice";
import { LongOrderPreparedPayloadRow } from "../../orderTypes";
import { FormValueTypes } from "../../types/longOrderTypes";
import { LongOrderForm } from "./LongOrderForm";
import { RowList } from "./RowList";
import { isoFormat } from "../../../../common/helpers/formatDateTime";
import { toTargetingRule } from "../../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/TargetedShiftsForm";

type Props = {
  title: string;
  defaultValues: FormValueTypes;
  updateOrderRowCount: (count: number) => void;
};

export const NewOrderLong = ({
  title,
  defaultValues,
  updateOrderRowCount,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation("common");
  const { confirm } = useConfirmationDialog();
  const isLoading = useIsLoading([
    "orderTemplates",
    "customerWorkLocations",
    "orderRows",
  ]);

  const COMMAND = "createLongOrder";

  const formDefault = { ...defaultValues, uid: nanoid() };

  const methods = useForm({
    defaultValues: formDefault,
    mode: "onChange",
  });
  const {
    getValues,
    watch,
    formState: { isDirty },
  } = methods;

  // One of orderRows is selected at any time, so that value is used in the form
  const [orderRows, setOrderRows] = useState<FormValueTypes[]>([formDefault]);
  const [formIsDirty, setFormIsDirty] = useState<boolean>(false);

  const handleAddNewRow = () => {
    // Add new row to state and select that to be edited
    const newRow = { ...defaultValues, uid: nanoid() };
    setOrderRows([...orderRows, newRow]);
    methods.reset(newRow);
  };

  const handleChangeRow = (uid: string) => {
    const row = orderRows.find((r) => r.uid === uid);
    if (row) {
      methods.reset(row, { keepDirty: true });
    }
  };

  // "Saves" row by marking the form as not dirty.
  // Order can be sent when required fields are filled and form is not dirty.
  const handleSaveRow = () => {
    const uid = getValues().uid;
    const row = orderRows.find((r) => r.uid === uid);
    if (row) {
      methods.reset(row);
    }
  };

  const handleRemoveRow = () => {
    const rowId = getValues().uid;
    const updatedOrderRows = orderRows.filter((row) => row.uid !== rowId);
    if (updatedOrderRows.length > 0) {
      setOrderRows(updatedOrderRows);
      const resetRow = updatedOrderRows[0];
      methods.reset(resetRow, { keepDirty: true });
    } else {
      const resetRow = { ...defaultValues, uid: nanoid() };
      setOrderRows([resetRow]);
      methods.reset(resetRow, { keepDirty: true });
    }
    // TODO scroll to top
  };

  const prepareOrderRows = (
    rows: FormValueTypes[],
  ): LongOrderPreparedPayloadRow[] => {
    return rows.map(
      (row) =>
        ({
          requiredTalentAmount: row.amount,
          contractId: row.orderTemplate?.contractId,
          customerOrgId: row.customerOrgId,
          customerCostCenterIds: row.customerCostCenters.map(
            (cc) => cc.customerCCId || cc.id,
          ),
          workLocationId: row.workLocation?.id,
          workTitleId: row.orderTemplate?.workTitleId,
          tesId: row.orderTemplate?.tesId,
          ...(row.type === OrderType.FixedTerm && {
            reasonForTemporaryEmployment: row.reasonForTemporaryEmployment,
          }),
          tesVariation: row.tesVariation,
          ...(!!row.orderTemplate?.periodStartDate && {
            periodStartDate: format(
              new Date(row.orderTemplate.periodStartDate),
              isoFormat,
            ),
          }),
          supplements: row.orderTemplate?.supplements,
          startDate: row.workStarts?.getTime(),
          endDate: row.workEnds?.getTime(),
          orderRowTemplateId: row.orderTemplate?.id,
          shortWorkTimeDescriptionFi: row.shortWorkTimeDescriptionFi,
          shortWorkTimeDescriptionEn: row.shortWorkTimeDescriptionEn,
          longWorkTimeDescriptionFi: row.longWorkTimeDescriptionFi,
          longWorkTimeDescriptionEn: row.longWorkTimeDescriptionEn,
          workDescriptionFi: row.workDescriptionFi,
          workDescriptionEn: row.workDescriptionEn,
          arrivalInstructionsFi: row.arrivalInstructionsFi,
          arrivalInstructionsEn: row.arrivalInstructionsEn,
          descriptionToEezyAndCustomer: row.descriptionToEezyAndCustomer,
          skills: row.skills.map((skill) => skill.id),
          languages: row.languages,
          certificates: row.certificates.map((certificate) => certificate.id),
          trainings: row.trainings.map((training) => training.id),
          supplementPriceOverrides: row.orderTemplate?.supplementPriceOverrides,
          targetedShiftTargetingRules: row.targetedShiftTargetingRules.map(
            (rule) => toTargetingRule(rule),
          ),
        }) as LongOrderPreparedPayloadRow,
    );
  };

  const onSubmit = async () => {
    setFormIsDirty(false);

    const preparedRows: LongOrderPreparedPayloadRow[] =
      prepareOrderRows(orderRows);
    const order = { title, orderRows: preparedRows };
    const hash = await generateOrderIdempotencyKey(preparedRows);

    const stored = getStoredIdempotencyHash(COMMAND);
    if (stored === hash) {
      const confirmed = await confirm({
        title: t("DUPLICATE_ORDER_TITLE"),
        contentText: t("DUPLICATE_ORDER_CONTENT"),
        confirmText: t("DUPLICATE_ORDER_CONFIRM"),
        cancelText: t("cancel"),
      });
      if (!confirmed) return;
    }

    const idempotencyKey = stored === hash ? undefined : hash;
    const action = await dispatch(addNewLongOrder({ order, idempotencyKey }));

    if (isFulfilled(action)) {
      storeIdempotencyHash(COMMAND, hash);
      navigate("/home/orders");
    }
  };

  // Update orderRows state when form values change
  const {
    uid,
    amount,
    customerOrgId,
    workLocation,
    orderTemplate,
    customerCostCenters,
    reasonForTemporaryEmployment,
    workStarts,
    workEnds,
    type,
    tesVariation,
    shortWorkTimeDescriptionFi,
    shortWorkTimeDescriptionEn,
    longWorkTimeDescriptionFi,
    longWorkTimeDescriptionEn,
    workDescriptionFi,
    workDescriptionEn,
    arrivalInstructionsFi,
    arrivalInstructionsEn,
    descriptionToEezyAndCustomer,
    skills,
    languages,
    certificates,
    trainings,
    targetedShiftTargetingRules,
  } = watch();

  useEffect(() => {
    setOrderRows((or) =>
      or.map((row) => (row.uid === uid ? { ...getValues() } : row)),
    );
  }, [
    uid,
    amount,
    customerOrgId,
    workLocation,
    orderTemplate,
    customerCostCenters,
    reasonForTemporaryEmployment,
    workStarts,
    workEnds,
    type,
    tesVariation,
    shortWorkTimeDescriptionFi,
    shortWorkTimeDescriptionEn,
    longWorkTimeDescriptionFi,
    longWorkTimeDescriptionEn,
    workDescriptionFi,
    workDescriptionEn,
    arrivalInstructionsFi,
    arrivalInstructionsEn,
    descriptionToEezyAndCustomer,
    skills,
    languages,
    certificates,
    trainings,
    getValues,
    targetedShiftTargetingRules,
  ]);

  useEffect(() => {
    setFormIsDirty(!isEqual(orderRows, [defaultValues]) || isDirty);
  }, [orderRows, defaultValues, isDirty]);

  useEffect(() => {
    updateOrderRowCount(orderRows.length);
  }, [orderRows, updateOrderRowCount]);

  const ROW_LIST: JSX.Element = (
    <RowList
      rows={orderRows}
      addRow={handleAddNewRow}
      changeRow={handleChangeRow}
      saveOrder={onSubmit}
    />
  );

  const FORM: JSX.Element = (
    <UnsavedChangesProvider isDirty={formIsDirty}>
      <LongOrderForm
        saveAction={handleSaveRow}
        addNewAction={handleAddNewRow}
        cancelAction={handleRemoveRow}
      />
    </UnsavedChangesProvider>
  );

  return (
    <Box sx={{ flexGrow: 1, minWidth: "1400px" }}>
      <OverlaySpinner
        sx={{ position: "fixed", width: "100%", height: "100%" }}
        isLoading={isLoading}
      />
      <FormProvider {...methods}>
        <PanelLayout
          leftPanelGridWidth={3}
          rightPanelGridWidth={9}
          leftWidget={ROW_LIST}
          rightWidget={FORM}
        />
      </FormProvider>
    </Box>
  );
};
